import React, {
  RefObject,
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { IconButton } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import _ from 'lodash';
import {
  selectCurrentScenario,
  selectScenarios,
  selectTotalPrograms,
} from 'spw-redux/modules/calculators/selectors';
import {
  selectScenarioIdsUsingSpaceType,
  selectWorksheetsUsingSpaceType,
} from 'spw-redux/modules/program-summary/programming/selectors';
import { IProjectSpaceType } from 'spw-redux/modules/space_types/types';
import {
  isDepartmentScenario,
  isFloorScenario,
} from 'spw-components/worksheet/utils';
import { numberWithCommas } from 'spw-shared/utils';
import { currentProject as selectCurrentProject } from 'spw-redux/modules/projects/selectors';
import {
  BUILDING_HEADCOUNT_SEATCOUNT,
  BUILDING_HEADCOUNT_SEATCOUNT_DEPARTMENTS,
  BUILDING_USF,
  CalculatorType,
  FLOOR_HEADCOUNT_SEATCOUNT,
  FLOOR_USF,
} from 'spw-redux/modules/calculators/types';
import { MACRO_PROGRAMMING_TYPE, TOTAL_PROGRAM_TYPE } from 'spw-constants';
import ChevronDown from 'spw-static/images/chevron-down-outlined.svg';
import ChevronUp from 'spw-static/images/chevron-up-outlined.svg';
import BlackLinkIcon from 'spw-static/images/black-link.svg';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { scenariosComparator } from 'spw-components/programming-scenarios/utils';
import { PlainProgramScenario } from '../../../../../models/Scenario';
import { ScenariosTableContainer, ScenariosTableCheckbox } from '../styles';

const STICKY_HEADER_HEIGHT = 48;
const TPS_INDEX_ON_EXP_STATE = 1;
const FLOORS_INDEX_ON_EXP_STATE = 4;

const getSecondaryTextCell = (s: PlainProgramScenario) => {
  if (isDepartmentScenario(s)) {
    return (
      <div className="cell secondary-text-cell">
        <div>{s?.department.name}</div>
        <div>{s?.department.secondaryIdentifier}</div>
      </div>
    );
  }

  if (isFloorScenario(s.calculatorType)) {
    return (
      <div className="cell secondary-text-cell">
        <div>{s?.building?.floors.find((f) => f.id === s?.floorId).name}</div>
      </div>
    );
  }

  return null;
};

const mapCalculatorTypeToExpArrayIndex = (calculatorType: CalculatorType) => {
  switch (calculatorType) {
    case MACRO_PROGRAMMING_TYPE:
      return 0;

    case BUILDING_HEADCOUNT_SEATCOUNT:
    case BUILDING_USF:
      return 3;

    case FLOOR_HEADCOUNT_SEATCOUNT:
    case FLOOR_USF:
      return 4;

    case TOTAL_PROGRAM_TYPE:
      return 1;

    default:
      return 2;
  }
};

const isFloorExpArrayIndex = (index: number) => {
  return index === FLOORS_INDEX_ON_EXP_STATE;
};

function isTpsExpArrayIndex(index: number) {
  return index === TPS_INDEX_ON_EXP_STATE;
}

interface Row {
  scenario: PlainProgramScenario;
  ref?: RefObject<HTMLDivElement>;
}

interface TPSExpansionState {
  totalProgram: PlainProgramScenario;
  ref: RefObject<HTMLDivElement>;
  expanded: boolean;
  children: Row[];
}

interface ExpansionState {
  group: string;
  expanded: boolean;
  children: (Row | TPSExpansionState)[];
}

const EXPANSION_ARRAY_PROTOTYPE = [
  {
    group: 'macro',
    expanded: false,
    children: [],
  },
  {
    group: 'totalProgram',
    expanded: false,
    children: [],
  },
  {
    group: 'department',
    expanded: false,
    children: [],
  },
  {
    group: 'building',
    expanded: false,
    children: [],
  },
  {
    group: 'floor',
    expanded: false,
    children: [],
  },
] as ExpansionState[];

const getGroupCheckedStatus = (
  es: ExpansionState,
  value: Record<number, boolean>
) => {
  return es.children.every((c) => {
    if (es.group === 'totalProgram') {
      return es.children.every((tpsEs: TPSExpansionState) =>
        tpsEs.children.every(({ scenario: s }) => value?.[s.id])
      );
    }

    const { scenario: s } = c as Row;
    return value?.[s.id];
  });
};

const getTpsCheckedStatus = (
  tpsEs: TPSExpansionState,
  value: Record<number, boolean>
) => {
  return (
    tpsEs.children.every(({ scenario: s }) => value?.[s.id]) &&
    !!tpsEs.children.length
  );
};

const childComparator = (childA: Row, childB: Row): number =>
  scenariosComparator(childA.scenario, childB.scenario);

export const ScenariosTable = ({
  onChange,
  maxHeight,
  subject,
  projectSpaceType,
}: {
  onChange: (newValue: Record<number, boolean>) => void;
  maxHeight: number;
  subject: 'USF' | 'Seats';
  projectSpaceType: Partial<IProjectSpaceType>;
}) => {
  const { convertArea } = useUnitConversion();
  const project = useSelector(selectCurrentProject);
  const scenarios = useSelector(selectScenarios);
  const currentScenario = useSelector(selectCurrentScenario);
  const totalPrograms = useSelector(selectTotalPrograms);
  const worksheets = useSelector(selectWorksheetsUsingSpaceType);
  const scenarioIdsUsingSpaceType = useSelector(
    selectScenarioIdsUsingSpaceType
  );

  const [value, setValue] = useState<Record<number, boolean>>();
  const [expansionStates, setExpansionStates] = useState<ExpansionState[]>();
  const [
    hasScrolledToCurrentScenarioRow,
    setHasScrolledToCurrentScenarioRow,
  ] = useState(false);

  const building = useMemo(() => project.buildings?.[0], [project]);
  const allChecked = useMemo(
    () => scenarioIdsUsingSpaceType.every((id) => value?.[id]),
    [scenarioIdsUsingSpaceType, value]
  );

  const tableContainerRef = useRef<HTMLDivElement>();

  // This useEffect initializes the expansion/collapsion and check/uncheck states
  useEffect(() => {
    if (
      currentScenario &&
      scenarioIdsUsingSpaceType &&
      scenarios &&
      building &&
      totalPrograms
    ) {
      const map = {} as Record<number, boolean>;
      let expansionArray = JSON.parse(
        JSON.stringify(EXPANSION_ARRAY_PROTOTYPE)
      ) as ExpansionState[];

      scenarioIdsUsingSpaceType.forEach((id) => {
        map[id] = false;
      });

      const tpsLinkedToUpdatableScenarios = totalPrograms.filter((tp) =>
        tp.linkedScenarios.some((s) => scenarioIdsUsingSpaceType.includes(s.id))
      );

      tpsLinkedToUpdatableScenarios.forEach((tp) => {
        expansionArray[TPS_INDEX_ON_EXP_STATE].children.push({
          totalProgram: tp,
          expanded: false,
          ref: createRef<HTMLDivElement>(),
          children: [],
        });
      });

      scenarios.forEach((s) => {
        if (!scenarioIdsUsingSpaceType.includes(s.id)) return;

        const expArrayIndex = mapCalculatorTypeToExpArrayIndex(
          s.calculatorType
        );
        expansionArray[expArrayIndex].children.push({
          scenario: s,
          ref: createRef<HTMLDivElement>(),
        });

        if (s.calculatorType === BUILDING_HEADCOUNT_SEATCOUNT_DEPARTMENTS) {
          const tpsToWhichScenarioIsLinked = tpsLinkedToUpdatableScenarios.filter(
            (_tp) => _tp.linkedScenarios.map((ls) => ls.id).includes(s.id)
          );

          tpsToWhichScenarioIsLinked.forEach((tp) => {
            (expansionArray[TPS_INDEX_ON_EXP_STATE]
              .children as TPSExpansionState[]).forEach(
              ({ totalProgram, children }) => {
                if (totalProgram.id === tp.id) {
                  children.push({
                    scenario: s,
                    ref: createRef<HTMLDivElement>(),
                  });
                }
              }
            );
          });
        }
      });

      const expArrayIndexForCurrentScenario = mapCalculatorTypeToExpArrayIndex(
        currentScenario.calculatorType
      );

      expansionArray[expArrayIndexForCurrentScenario].expanded = true;

      if (currentScenario.calculatorType === TOTAL_PROGRAM_TYPE) {
        currentScenario.linkedScenarios.forEach((s) => {
          map[s.id] = true;
        });

        const tpsExpansionStateIndex = (expansionArray[TPS_INDEX_ON_EXP_STATE]
          .children as TPSExpansionState[]).findIndex(
          ({ totalProgram }) => totalProgram.id === currentScenario.id
        );

        (expansionArray[TPS_INDEX_ON_EXP_STATE]
          .children as TPSExpansionState[])[
          tpsExpansionStateIndex
        ].expanded = true;
      }

      if (currentScenario.calculatorType !== TOTAL_PROGRAM_TYPE) {
        map[currentScenario.id] = true;
      }

      expansionArray = expansionArray.map((es, i) => {
        if (isTpsExpArrayIndex(i)) return es;

        const children = es.children as Row[];
        let sortedChildren = [] as Row[];

        if (isFloorExpArrayIndex(i)) {
          for (let j = 0; j < building?.floors.length; j++) {
            const f = building?.floors[j];

            const childrenOfThisFloor = children.filter(
              ({ scenario }) => scenario.floorId === f.id
            );

            sortedChildren = [
              ...sortedChildren,
              ...childrenOfThisFloor.sort(childComparator),
            ];
          }
        } else {
          sortedChildren = [
            ...sortedChildren,
            ...children.sort(childComparator),
          ];
        }

        return {
          ...es,
          children: sortedChildren,
        };
      });

      setValue(map);
      setExpansionStates(expansionArray);
    }

    return () => {
      if (
        currentScenario &&
        scenarioIdsUsingSpaceType &&
        scenarios &&
        totalPrograms
      ) {
        setValue(undefined);
        setExpansionStates(undefined);
        setHasScrolledToCurrentScenarioRow(false);
      }
    };
  }, [
    currentScenario,
    scenarioIdsUsingSpaceType,
    scenarios,
    totalPrograms,
    building,
  ]);

  // This useEffect is responsible for scrolling to the currentScenario row at the beginning
  useEffect(() => {
    if (
      tableContainerRef.current &&
      currentScenario &&
      expansionStates &&
      !hasScrolledToCurrentScenarioRow
    ) {
      let row: HTMLDivElement;

      if (currentScenario.calculatorType === TOTAL_PROGRAM_TYPE) {
        const tpsExpansionState = expansionStates[
          TPS_INDEX_ON_EXP_STATE
        ].children.find(
          ({ totalProgram }: TPSExpansionState) =>
            totalProgram.id === currentScenario.id
        ) as TPSExpansionState;

        row = tpsExpansionState.ref.current;
      } else {
        const index = mapCalculatorTypeToExpArrayIndex(
          currentScenario.calculatorType
        );
        row = expansionStates[index].children.find(
          ({ scenario: s }: Row) => s.id === currentScenario.id
        ).ref.current;
      }

      const elementRect = row.getBoundingClientRect();
      const containerRect = tableContainerRef.current.getBoundingClientRect();
      const distance =
        elementRect.top - containerRect.top - STICKY_HEADER_HEIGHT;

      tableContainerRef.current.scroll({ top: distance });
      setHasScrolledToCurrentScenarioRow(true);
    }
  }, [
    tableContainerRef,
    currentScenario,
    expansionStates,
    hasScrolledToCurrentScenarioRow,
    worksheets,
  ]);

  useEffect(() => {
    onChange(value);
  }, [onChange, value]);

  const handleToggleAllScenario = useCallback(() => {
    setValue((pv) => {
      const nv = { ...pv };

      for (const key in nv) {
        if (Object.prototype.hasOwnProperty.call(nv, key)) {
          nv[key] = !allChecked;
        }
      }

      return nv;
    });
  }, [allChecked]);

  const handleToggleGroup = useCallback((es: ExpansionState) => {
    setValue((pv) => {
      const nv = { ...pv };

      const groupCheckedStatus = getGroupCheckedStatus(es, pv);

      if (es.group === 'totalProgram') {
        es.children.forEach((tpsEs: TPSExpansionState) =>
          tpsEs.children.forEach(({ scenario: s }) => {
            nv[s.id] = !groupCheckedStatus;
          })
        );

        return nv;
      }

      es.children.forEach(({ scenario: s }: Row) => {
        nv[s.id] = !groupCheckedStatus;
      });

      return nv;
    });
  }, []);

  const handleToggleTps = useCallback((tpsEs: TPSExpansionState) => {
    setValue((pv) => {
      const nv = { ...pv };

      const tpsCheckedStatus = getTpsCheckedStatus(tpsEs, pv);

      tpsEs.children.forEach(({ scenario: s }) => {
        nv[s.id] = !tpsCheckedStatus;
      });

      return nv;
    });
  }, []);

  const handleToggleScenario = useCallback((scenarioId: number) => {
    setValue((pv) => {
      const nv = { ...pv };

      nv[scenarioId] = !nv[scenarioId];

      return nv;
    });
  }, []);

  const getScenarioSubjectValue = useCallback(
    (scenario: PlainProgramScenario) => {
      const _worksheet = worksheets.find(
        (ws) => ws.scenario.id === scenario.id
      );
      const row = _worksheet?.rows.find(
        (r) => r.projectSpaceType.id === projectSpaceType.id
      );

      if (!row || row.removed) return '0';

      const subjectValue =
        subject === 'USF' ? convertArea(row.usf) : row.seatCap;

      return numberWithCommas(subjectValue.toFixed(0));
    },
    [subject, worksheets, projectSpaceType.id, convertArea]
  );

  if (!expansionStates) return null;

  return (
    <ScenariosTableContainer style={{ maxHeight }} ref={tableContainerRef}>
      <div className="header">
        <div className="row">
          <div className="cell checkbox-cell">
            <ScenariosTableCheckbox
              checked={allChecked}
              onChange={handleToggleAllScenario}
            />
          </div>
          <div className="cell text-cell">UPDATE ALL SCENARIOS</div>
        </div>
      </div>
      <div className="body">
        {expansionStates
          .filter(({ children }) => children.length)
          .map((es) => {
            const i = expansionStates.indexOf(es);

            return (
              <div key={es.group}>
                <div className="row">
                  <div className="cell checkbox-cell">
                    <ScenariosTableCheckbox
                      checked={getGroupCheckedStatus(es, value)}
                      onChange={() => handleToggleGroup(es)}
                    />
                  </div>
                  <div className="cell text-cell">
                    <IconButton
                      onClick={() => {
                        setExpansionStates((prevArray) => {
                          const newArray = [...prevArray];
                          newArray[i].expanded = !newArray[i].expanded;

                          return newArray;
                        });
                      }}
                    >
                      {es.expanded ? (
                        <img src={ChevronUp} alt="collapse" />
                      ) : (
                        <img src={ChevronDown} alt="expand" />
                      )}
                    </IconButton>
                    {_.startCase(es.group).toUpperCase()}
                  </div>
                </div>
                {es.expanded &&
                  es.group !== 'totalProgram' &&
                  es.children.map((r: Row) => {
                    const { scenario: s, ref } = r;
                    let isLinkedDeptScenario = false;

                    if (isDepartmentScenario(s)) {
                      isLinkedDeptScenario = expansionStates[
                        TPS_INDEX_ON_EXP_STATE
                      ]?.children.some((tpsEs: TPSExpansionState) =>
                        tpsEs.children?.find(
                          ({ scenario: _s }) => _s.id === s.id
                        )
                      );
                    }

                    return (
                      <div key={`scenario-${s.id}`} ref={ref}>
                        <div className="row nested-row">
                          <div className="cell checkbox-cell">
                            <ScenariosTableCheckbox
                              checked={value[s.id]}
                              onChange={() => handleToggleScenario(s.id)}
                            />
                          </div>
                          {isLinkedDeptScenario && (
                            <div className="cell icon-cell">
                              <img
                                src={BlackLinkIcon}
                                alt="department linked"
                              />
                            </div>
                          )}
                          <div className="cell text-cell">{s.name}</div>
                          {getSecondaryTextCell(s)}
                          <div className="cell value-cell">
                            {getScenarioSubjectValue(s)}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                {es.expanded &&
                  es.group === 'totalProgram' &&
                  es.children
                    .filter((tpsEs: TPSExpansionState) => tpsEs.children.length)
                    .map((tpsEs: TPSExpansionState) => (
                      <div
                        key={`tps-scenario-${tpsEs.totalProgram.id}`}
                        ref={tpsEs.ref}
                      >
                        <div className="row tps-nested-row">
                          <div className="cell checkbox-cell">
                            <ScenariosTableCheckbox
                              checked={getTpsCheckedStatus(tpsEs, value)}
                              onChange={() => handleToggleTps(tpsEs)}
                            />
                          </div>
                          <div className="cell text-cell">
                            <IconButton
                              onClick={() => {
                                setExpansionStates((prevArray) => {
                                  const newArray = [...prevArray];
                                  const j = es.children.indexOf(tpsEs);

                                  const children = newArray[i]
                                    .children as TPSExpansionState[];
                                  children[j].expanded = !children[j].expanded;
                                  newArray[i].children = children;

                                  return newArray;
                                });
                              }}
                            >
                              {tpsEs.expanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                            {tpsEs.totalProgram.name}
                          </div>
                          <div className="cell secondary-text-cell">
                            <div>
                              {tpsEs.children.length} of{' '}
                              {tpsEs.totalProgram.linkedScenarios.length} linked
                              scenario
                              {tpsEs.totalProgram.linkedScenarios.length > 1
                                ? 's'
                                : ''}
                            </div>
                          </div>
                        </div>
                        {tpsEs.expanded &&
                          tpsEs.children.map(({ scenario: s }) => (
                            <div
                              key={`tps-scenario-${tpsEs.totalProgram.id}-scenario-${s.id}`}
                              className="row nested-row"
                            >
                              <div className="cell checkbox-cell">
                                <ScenariosTableCheckbox
                                  checked={value[s.id]}
                                  onChange={() => handleToggleScenario(s.id)}
                                />
                              </div>
                              <div className="cell text-cell">{s.name}</div>
                              <div className="cell secondary-text-cell">
                                <div>{s?.department.name}</div>
                                <div>{s?.department.secondaryIdentifier}</div>
                              </div>
                              <div className="cell value-cell">
                                {getScenarioSubjectValue(s)}
                              </div>
                            </div>
                          ))}
                      </div>
                    ))}
              </div>
            );
          })}
      </div>
    </ScenariosTableContainer>
  );
};
